import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Target, Users, BookOpen, ArrowRight, GraduationCap } from "lucide-react";
import { PageHero } from "@/components/Decor";
import { EQUIPE } from "@/lib/data";

const KIDS_IMG = "/centre-entree.webp";

export default function CentrePage() {
  return (
    <>
      <PageHero
        eyebrow="Le Centre"
        title={<>Un lieu d'éveil <em className="italic text-coral">au cœur de Papara</em></>}
        subtitle="Depuis plusieurs années, nous accompagnons les enfants de Papara et Mataiea après l'école dans un cadre bienveillant et stimulant."
      />

      {/* Mot du directeur enrichi */}
      <section className="relative py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5 relative"
          >
            <div className="relative h-[460px] rounded-3xl overflow-hidden card-3d">
              <img src={KIDS_IMG} alt="Entrée du Centre de Soutien Scolaire Papara" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-ocean/25 to-transparent" />
            </div>
            <div className="absolute -bottom-6 -right-4 sm:right-6 glass rounded-2xl px-5 py-4">
              <div className="text-xs uppercase tracking-widest text-coral font-semibold">Depuis</div>
              <div className="font-display text-3xl text-ocean">2024</div>
            </div>
          </motion.div>
          <div className="lg:col-span-7">
            <div className="text-xs uppercase tracking-[0.25em] text-coral font-semibold mb-4">Notre histoire</div>
            <h2 className="font-display text-4xl text-ocean leading-tight mb-6">
              Une équipe passionnée par l'éducation
            </h2>
            <p className="text-ocean/70 leading-relaxed mb-4">
              Le Centre de Soutien Scolaire Papara est né d'une conviction simple : chaque enfant mérite
              un accompagnement personnalisé pour s'épanouir scolairement et personnellement.
            </p>
            <p className="text-ocean/70 leading-relaxed mb-4">
              Nous accueillons les élèves de la maternelle au collège, à la sortie de leur école, et leur offrons
              un espace structuré où ils peuvent faire leurs devoirs, réviser, mais aussi jouer,
              lire et pratiquer des activités culturelles.
            </p>
            <p className="text-ocean/70 leading-relaxed">
              Notre objectif : <strong className="text-ocean">sociabiliser, apprendre, s'épanouir</strong> — et diminuer
              l'exposition aux écrans.
            </p>
          </div>
        </div>
      </section>

      {/* Valeurs */}
      <section className="relative py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="text-xs uppercase tracking-[0.25em] text-coral font-semibold mb-4">Nos valeurs</div>
            <h2 className="font-display text-4xl text-ocean">Ce qui nous anime au quotidien</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: Heart, title: "Bienveillance", desc: "Une oreille attentive et patiente pour chaque enfant." },
              { icon: Target, title: "Exigence", desc: "Des méthodes rigoureuses et adaptées à chaque niveau." },
              { icon: Users, title: "Communauté", desc: "Un lien fort avec les familles et les écoles partenaires." },
              { icon: BookOpen, title: "Curiosité", desc: "Éveiller la passion d'apprendre par le jeu et la culture." },
            ].map((v, i) => {
              const Icon = v.icon;
              return (
                <motion.div
                  key={v.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="bg-white rounded-3xl p-7 border border-ocean/5 card-3d"
                >
                  <div className="w-12 h-12 rounded-2xl bg-coral/10 text-coral flex items-center justify-center mb-5">
                    <Icon size={22} />
                  </div>
                  <h3 className="font-display text-xl text-ocean mb-2">{v.title}</h3>
                  <p className="text-sm text-ocean/60 leading-relaxed">{v.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* NOTRE ÉQUIPE */}
      <section className="relative py-24 bg-gradient-to-b from-transparent via-sand to-transparent">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="text-xs uppercase tracking-[0.25em] text-coral font-semibold mb-4">Notre équipe</div>
            <h2 className="font-display text-4xl sm:text-5xl text-ocean leading-tight">
              Les visages du <em className="italic text-coral">Centre</em>
            </h2>
            <p className="mt-5 text-ocean/65 text-base leading-relaxed">
              Une équipe pluridisciplinaire, diplômée et passionnée, à l'écoute de chaque enfant selon son âge et son niveau.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5">
            {EQUIPE.map((m, i) => (
              <motion.div
                key={m.prenom}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="group relative bg-white rounded-3xl overflow-hidden border border-ocean/5 card-3d"
                data-testid={`equipe-${m.prenom.toLowerCase()}`}
              >
                <div className="relative aspect-[4/5] overflow-hidden bg-sand">
                  <img
                    src={m.photo}
                    alt={`${m.prenom} — ${m.role}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-ocean/70 via-transparent to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                    <div className="font-display text-2xl leading-tight">{m.prenom}</div>
                    <div className="text-xs text-white/80 mt-0.5">{m.role}</div>
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-1.5 mb-2 text-[10px] uppercase tracking-widest text-coral font-semibold">
                    <GraduationCap size={12} /> Diplômes
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {m.diplomes.map((d) => (
                      <span key={d} className="text-[11px] px-2 py-1 rounded-full bg-ocean/5 text-ocean/80 font-medium">
                        {d}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-4xl text-ocean mb-5">
            Envie de découvrir notre centre ?
          </h2>
          <p className="text-ocean/65 max-w-xl mx-auto mb-8">
            Contactez-nous pour organiser une visite ou poser vos questions. Nous serons ravis de vous accueillir.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="btn-pill btn-primary" data-testid="centre-cta-contact">
              Nous contacter <ArrowRight size={18} />
            </Link>
            <Link to="/inscription" className="btn-pill btn-accent" data-testid="centre-cta-inscription">
              Inscrire mon enfant
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
